import type { EarliestConsumptionPerLocationDTO } from '@/src/api/types/procurement'
import { parseIsoAsWallClock } from '@/src/utils/dateUtil'
import { format, isBefore } from 'date-fns'
import type { TFunction } from 'i18next'

export const LAST_365_DAYS = 'last-365-days'
export const LAST_365_DAYS_UPDATED = 'last-365-days-updated'
export type ReferenceTimePeriodOption = {
  label: string
  value: string
  startDate: string
  endDate: string
  tagLabel: string | null
  tagVariant?: 'lightGreen' | 'lightBlue' | 'lightAmber'
}

type ReferenceTimePeriodOptionBase = Pick<ReferenceTimePeriodOption, 'label' | 'value'>

const formatLast365OptionLabel = (t: TFunction, periodLabel: string) =>
  `${t('components.procurement.forecasting.reference_period_modal.last_365_days')} (${periodLabel})`

const getLast365TagLabel = (t: TFunction, value: string, shouldShowLast365Days: boolean) => {
  if (!shouldShowLast365Days) return null
  if (value === LAST_365_DAYS) return t('components.procurement.forecasting.reference_period_modal.reference_period_tag_new_available')
  if (value === LAST_365_DAYS_UPDATED) return t('components.procurement.forecasting.reference_period_modal.reference_period_tag_outdated')
  return null
}

export const isOutdatedLast365ReferenceDate = (referenceDate?: string | null, latestReferenceDateTime?: string | null) => {
  if (!latestReferenceDateTime) return false

  const periodType = getReferenceDatePeriodType(referenceDate)
  if (periodType !== 'last-365-days') return false

  return !isReferenceDateUpToDate(referenceDate, latestReferenceDateTime)
}

export type ReferenceDatePeriodType = 'full-year' | 'last-365-days' | null

export const getReferenceDatePeriodType = (referenceDate?: string | null): ReferenceDatePeriodType => {
  if (!referenceDate) return null

  const parsedDate = new Date(referenceDate)
  if (Number.isNaN(parsedDate.getTime())) return null

  const month = parsedDate.getMonth() + 1
  const day = parsedDate.getDate()

  return month === 1 && day === 1 ? 'full-year' : 'last-365-days'
}

export const isReferenceDateUpToDate = (referenceDate?: string | null, latestReferenceDateTime?: string | null) => {
  if (!referenceDate || !latestReferenceDateTime) return true

  const parsedReferenceDate = new Date(referenceDate)
  if (Number.isNaN(parsedReferenceDate.getTime())) return false

  const parsedLatest = new Date(latestReferenceDateTime)
  if (Number.isNaN(parsedLatest.getTime())) return true

  const referenceLocalDay = new Date(parsedReferenceDate.getFullYear(), parsedReferenceDate.getMonth(), parsedReferenceDate.getDate())
  const latestLocalDay = new Date(parsedLatest.getFullYear(), parsedLatest.getMonth(), parsedLatest.getDate())

  return !isBefore(referenceLocalDay, latestLocalDay)
}

export type ReferencePeriodDates = {
  startDate: Date
  endDate: Date
}

export const getReferencePeriodDatesFromReferenceDate = (referenceDate?: string | null): ReferencePeriodDates | null => {
  if (!referenceDate) return null

  const parsedReferenceDate = new Date(referenceDate)
  if (Number.isNaN(parsedReferenceDate.getTime())) return null

  const periodType = getReferenceDatePeriodType(referenceDate)
  if (!periodType) return null

  let startDate: Date
  let endDate: Date
  if (periodType === 'full-year') {
    const fullYear = parsedReferenceDate.getFullYear() - 1
    startDate = new Date(fullYear, 0, 1)
    endDate = new Date(fullYear, 11, 31)
  } else {
    endDate = new Date(parsedReferenceDate.getFullYear(), parsedReferenceDate.getMonth(), parsedReferenceDate.getDate())
    endDate.setDate(endDate.getDate() - 1)
    startDate = new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate())
    startDate.setDate(startDate.getDate() + 1)
  }

  return { startDate, endDate }
}

export const getUpToDateLast365PeriodDates = (latestReferenceDateTime?: string | null): ReferencePeriodDates | null =>
  getReferencePeriodDatesFromReferenceDate(latestReferenceDateTime)

export const getReferencePeriodLabel = (referenceDate?: string | null) => {
  const dates = getReferencePeriodDatesFromReferenceDate(referenceDate)
  if (!dates) return '-'

  return `${format(dates.startDate, 'dd-MM-yyyy')} - ${format(dates.endDate, 'dd-MM-yyyy')}`
}

export const getUpToDateLast365PeriodLabel = (latestReferenceDateTime?: string | null) => {
  const dates = getUpToDateLast365PeriodDates(latestReferenceDateTime)
  if (!dates) return '-'

  return `${format(dates.startDate, 'dd-MM-yyyy')} - ${format(dates.endDate, 'dd-MM-yyyy')}`
}

export type ReferenceYearOption = { year: number }

const getLocationConsumptionStartYear = (earliestConsumptionDateTime: string): number | null => {
  const parsed = parseIsoAsWallClock(earliestConsumptionDateTime)
  if (Number.isNaN(parsed.getTime())) return null

  const year = parsed.getFullYear()
  if (parsed.getMonth() === 11 && parsed.getDate() === 31) {
    return year + 1
  }

  return year
}

export const getReferenceYearsFromEarliestConsumption = (
  earliestConsumption?: EarliestConsumptionPerLocationDTO[] | null
): ReferenceYearOption[] => {
  if (!earliestConsumption?.length) return []

  const locationStartYears = earliestConsumption
    .map(entry => getLocationConsumptionStartYear(entry.earliestConsumptionDateTime))
    .filter((year): year is number => year !== null)

  if (!locationStartYears.length) return []

  const startYear = Math.min(...locationStartYears)
  const endYear = new Date().getFullYear() - 1

  if (startYear > endYear) return []

  const options: ReferenceYearOption[] = []
  for (let year = startYear; year <= endYear; year++) {
    options.push({ year })
  }

  return options.sort((a, b) => b.year - a.year)
}

const isLatestReferenceAtYearStart = (latestReferenceDateTime?: string | null) => {
  if (!latestReferenceDateTime) return false
  const parsed = new Date(latestReferenceDateTime)
  if (Number.isNaN(parsed.getTime())) return false
  return parsed.getMonth() === 0 && parsed.getDate() === 1
}

export const getOptionValueFromReferenceDate = (referenceDate?: string | null, latestReferenceDateTime?: string | null) => {
  const periodType = getReferenceDatePeriodType(referenceDate)

  if (periodType === 'full-year') {
    const parsedReferenceDate = new Date(referenceDate ?? '')
    return String(parsedReferenceDate.getFullYear() - 1)
  }

  if (periodType === 'last-365-days') {
    return isReferenceDateUpToDate(referenceDate, latestReferenceDateTime) ? LAST_365_DAYS : LAST_365_DAYS_UPDATED
  }

  return LAST_365_DAYS
}

const parseYearOptionValue = (value: string) => (/^\d{4}$/.test(value) ? Number(value) : null)

export const isYearOptionValue = (value?: string) => (value ? parseYearOptionValue(value) !== null : false)

export const getReferenceRequestDateFromOptionValue = (
  value: string,
  referenceDate?: string | null,
  latestReferenceDateTime?: string | null
) => {
  const parsedYear = parseYearOptionValue(value)
  if (parsedYear) return `${parsedYear + 1}-01-01`

  if (value === LAST_365_DAYS_UPDATED) {
    const parsedReferenceDate = new Date(referenceDate ?? '')
    if (!Number.isNaN(parsedReferenceDate.getTime())) {
      return format(parsedReferenceDate, 'yyyy-MM-dd')
    }
  }

  const parsedLatest = latestReferenceDateTime ? new Date(latestReferenceDateTime) : null
  if (parsedLatest && !Number.isNaN(parsedLatest.getTime())) {
    return format(parsedLatest, 'yyyy-MM-dd')
  }

  const parsedReferenceDate = referenceDate ? new Date(referenceDate) : null
  if (parsedReferenceDate && !Number.isNaN(parsedReferenceDate.getTime())) {
    return format(parsedReferenceDate, 'yyyy-MM-dd')
  }

  throw new Error('Missing latestReferenceDateTime for last 365 days reference period')
}

export const getReferencePeriodDatesFromOptionValue = (
  value: string,
  referenceDate?: string | null,
  latestReferenceDateTime?: string | null
): ReferencePeriodDates | null => {
  const parsedYear = parseYearOptionValue(value)
  if (parsedYear) {
    return {
      startDate: new Date(parsedYear, 0, 1),
      endDate: new Date(parsedYear, 11, 31),
    }
  }

  const requestDate = getReferenceRequestDateFromOptionValue(value, referenceDate, latestReferenceDateTime)
  return getReferencePeriodDatesFromReferenceDate(requestDate)
}

const buildReferenceTimePeriodOption = (
  option: ReferenceTimePeriodOptionBase,
  shouldShowLast365Days: boolean,
  t: TFunction,
  referenceDate?: string | null,
  latestReferenceDateTime?: string | null
): ReferenceTimePeriodOption => {
  const periodDates = getReferencePeriodDatesFromOptionValue(option.value, referenceDate, latestReferenceDateTime)
  const tagLabel = getLast365TagLabel(t, option.value, shouldShowLast365Days)
  const tagVariant = tagLabel ? (option.value === LAST_365_DAYS ? 'lightGreen' : 'lightBlue') : undefined

  return {
    ...option,
    startDate: periodDates ? format(periodDates.startDate, 'yyyy-MM-dd') : '',
    endDate: periodDates ? format(periodDates.endDate, 'yyyy-MM-dd') : '',
    tagLabel,
    tagVariant,
  }
}

export const getReferenceTimePeriodOptions = (
  t: TFunction,
  referenceDate?: string | null,
  years: ReferenceYearOption[] = [],
  latestReferenceDateTime?: string | null
): ReferenceTimePeriodOption[] => {
  const yearOptions: ReferenceTimePeriodOptionBase[] = years.map(option => ({
    label: t('components.procurement.forecasting.reference_period_modal.calendar_year_label', { year: option.year }),
    value: String(option.year),
  }))

  if (isLatestReferenceAtYearStart(latestReferenceDateTime)) {
    return yearOptions.map(option => buildReferenceTimePeriodOption(option, false, t, referenceDate, latestReferenceDateTime))
  }

  const upToDateLast365Option: ReferenceTimePeriodOptionBase = {
    label: formatLast365OptionLabel(t, getUpToDateLast365PeriodLabel(latestReferenceDateTime)),
    value: LAST_365_DAYS,
  }

  const shouldShowOutdatedLast365Option = isOutdatedLast365ReferenceDate(referenceDate, latestReferenceDateTime)
  const outdatedLast365Option: ReferenceTimePeriodOptionBase | null = shouldShowOutdatedLast365Option
    ? {
        label: formatLast365OptionLabel(t, getReferencePeriodLabel(referenceDate)),
        value: LAST_365_DAYS_UPDATED,
      }
    : null

  const shouldShowLast365Days = !!outdatedLast365Option
  const baseOptions = outdatedLast365Option
    ? [outdatedLast365Option, upToDateLast365Option, ...yearOptions]
    : [upToDateLast365Option, ...yearOptions]

  return baseOptions.map(option => buildReferenceTimePeriodOption(option, shouldShowLast365Days, t, referenceDate, latestReferenceDateTime))
}

export const getOrderedReferenceTimePeriodOptions = (options: ReferenceTimePeriodOption[], selectedReferenceOptionValue?: string) => {
  if (!selectedReferenceOptionValue) return options
  const selectedIndex = options.findIndex(option => option.value === selectedReferenceOptionValue)
  if (selectedIndex <= 0) return options
  const selectedOption = options[selectedIndex]
  return [selectedOption, ...options.slice(0, selectedIndex), ...options.slice(selectedIndex + 1)]
}
